import { useClient } from '@playbooks/contexts';
import { Link } from '@playbooks/ui/components/link';
import { Progress } from '@playbooks/ui/components/progress';
import { cn } from '@playbooks/ui/lib/utils';
import { formatDate, getSubscriptionUsage } from '@playbooks/utils';

const AccountDropUsage = ({ account }) => {
	const { platformHref } = useClient();
	const subscription = account.subscription || {};
	const usage = getSubscriptionUsage(subscription, { isFree: account.isFree });

	if (!usage.hasUsage) return null;

	const used = Number(usage.usedCredits);
	const total = Number(usage.totalCredits);
	const progress = Math.min(usage.percentUsed, 100);
	const resetDate = usage.endDate ? formatDate(usage.endDate, 'MMM D') : '';
	const status = usage.isDepleted
		? `Projects paused${resetDate ? ` · resets ${resetDate}` : ''}`
		: usage.isOverage
			? `${Number(usage.overageCredits).toLocaleString()} over${resetDate ? ` · resets ${resetDate}` : ''}`
			: resetDate
				? `Resets ${resetDate}`
				: '';
	const tone = usage.isDepleted ? 'danger' : usage.isOverage ? 'warning' : 'action';
	const classNames = {
		value: cn(
			'text-xs font-semibold leading-4 tabular-nums text-foreground',
			tone === 'warning' && 'text-warning',
			tone === 'danger' && 'text-danger',
		),
		progress: 'gap-0 [&_[data-slot=progress-track]]:h-1 [&_[data-slot=progress-track]]:bg-surface-selected',
		status: cn(
			'min-w-0 truncate whitespace-nowrap text-[11px] leading-4 text-foreground-subtle',
			tone === 'warning' && 'text-warning',
			tone === 'danger' && 'text-danger',
		),
	};

	return (
		<div className='border-b border-border px-3 py-2.5'>
			<div className='flex-between gap-3'>
				<span className='text-xs font-medium leading-4 text-foreground-body'>Credits</span>
				<span className={classNames.value}>
					{used.toLocaleString()} / {total.toLocaleString()}
				</span>
			</div>
			<Progress value={progress} tone={tone} className={cn('mt-1.5', classNames.progress)} />
			<div className='mt-1 flex-between gap-2 leading-none'>
				<span className={classNames.status}>{status}</span>
				<Link
					data-menu-contained
					variant='link'
					size='xs'
					href={platformHref('/settings/workspace/billing')}
					className='h-auto shrink-0 p-0 text-[11px] leading-4 font-medium text-foreground-body hover:text-foreground'>
					Manage plan
				</Link>
			</div>
		</div>
	);
};

export { AccountDropUsage };
